<script setup>

import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";
import { reactive, ref } from "vue";
import { useMutation, useQueryClient } from "@tanstack/vue-query";
import axios from "axios";
import {errorToast, successToast} from "@/composables/useToasts";
import AlertBox from "@/components/global-themed/AlertBox.vue";
import NumberInput from "@/components/global-themed/inputs/NumberInput.vue";
import CustomMultiselect from "@/components/global-themed/CustomMultiselect.vue";
import DefaultInput from "@/components/global-themed/inputs/DefaultInput.vue";
import DefaultTextarea from "@/components/global-themed/inputs/DefaultTextarea.vue";

const showAlert = ref(true);

const toggleAlert = () => {
  showAlert.value = !showAlert.value;
}

const queryClient = useQueryClient();

const form = reactive({
  title: null,
  description: null,
  priority: 'select',
  time_required: null,
});

const formErrors = ref({});

const priorityOptions = [
  {
    label: 'Low',
    value: 1,
  },
  {
    label: 'Medium',
    value: 7,
  },
  {
    label: 'High',
    value: 10,
  }
]

const { mutate: createAction, isPending, isSuccess } = useMutation({
  mutationFn: function () {
    formErrors.value = {};

    return axios.post('/ajax/v2/actions/store', form);
  },
  onSuccess: () => {
    // Invalidate queries to refresh frontend data
    queryClient.invalidateQueries({ queryKey: ['actions'] });
    queryClient.invalidateQueries({ queryKey: ['action-categories'] });
    queryClient.invalidateQueries({ queryKey: ['action-progress'] });
    
    setTimeout(() => {
      window.location.href = '/actions';
    }, 1500);

    successToast("Action created successfully.");
  },
  onError: (error) => {
    if (error.response.status === 422) {
      formErrors.value = error.response.data.errors;
    } else {
      errorToast('Something went wrong creating action');
    }
  },
})
</script>

<template>
  <div class="tw-m-auto tw-max-w-default-container">
    <div class="tw-flex tw-flex-center tw-justify-between tw-mb-8">
      <div>
        <h1 class="tw-font-bold tw-text-4.5xl tw-mb-4">
          Add a Custom Action
        </h1>

        <span class="tw-text-neutral-900 tw-text-md tw-text-base">
          Add your own action below and it will appear in your customer’s list of actions for this month.
        </span>
      </div>
      <div class="tw-flex tw-items-stretch tw-gap-4 tw-h-13">
        <DefaultButton
          variant="primary-inverted"
          as="a"
          href="/actions"
          class="tw-h-13"
        >
          <svg-vue
            icon="icons/back-arrow"
            class="tw-w-6 tw-h-6 tw-mr-3"
          />
          <span class="tw-font-normal">Back</span>
        </DefaultButton>
      </div>
    </div>

    <div class="tw-grid tw-grid-cols-3 tw-gap-8">
      <section class="tw-bg-white tw-p-8 tw-rounded-lg">
        <div class="tw-mb-8">
          <label
            for="select-priority"
            class="tw-text-neutral-900 tw-font-bold tw-text-2xl tw-mb-4"
          >
            Select Priority
          </label>
          <CustomMultiselect
            id="select-priority"
            v-model="form.priority"
            :options="priorityOptions"
            :close-on-select="true"
            :has-error="formErrors.priority && formErrors.priority.length > 0"
          />

          <AlertBox
            v-if="formErrors.priority"
            type="danger"
            class="tw-mt-4 tw-text-base"
          >
            {{ formErrors.priority[0] }}
          </AlertBox>
        </div>

        <div>
          <label
            for="time-required"
            class="tw-text-neutral-900 tw-font-bold tw-text-2xl tw-mb-4"
          >
            Time Required (in minutes)
          </label>
          <NumberInput
            id="time-required"
            v-model="form.time_required"
            name="time-required"
            :validation-errors="formErrors.time_required"
            min="1"
            placeholder="Enter time"
          />
          <AlertBox
            v-if="formErrors.time_required"
            type="danger"
            class="tw-mt-4 tw-text-base"
          >
            {{ formErrors.time_required[0] }}
          </AlertBox>
        </div>
      </section>
      <section class="tw-bg-white tw-col-span-2 tw-p-8 tw-rounded-lg">
        <AlertBox
          v-if="showAlert"
          type="info"
          dismissable
          class="tw-mb-8 tw-text-base"
          @dismiss="toggleAlert"
        >
          Please note, manual actions will be prioritised over all other actions, so ensure that the amount of time
          you set for actions is large enough to accommodate
          both manual actions and system generated actions. e.g. If you add 4 hours of manual actions and the amount
          of time you set for actions each month is 4 hours
          you will only have manual actions show.
        </AlertBox>

        <div class="tw-mb-8">
          <label
            for="action-title"
            class="tw-text-neutral-900 tw-font-bold tw-text-2xl tw-mb-4"
          >
            Action Title
          </label>

          <DefaultInput
            id="action-title"
            v-model="form.title"
            :validation-errors="formErrors.title"
            name="action-title"
          />
        </div>

        <label
          for="action-description"
          class="tw-text-neutral-900 tw-font-bold tw-text-2xl tw-mb-4"
        >
          Action Description
        </label>
        <DefaultTextarea
          id="action-description"
          v-model="form.description"
          placeholder="Enter your action description here."
          name="action-description"
          :validation-errors="formErrors.description"
        />

        <div class="tw-flex tw-justify-end tw-mt-8">
          <DefaultButton
            :disabled="isPending || isSuccess"
            :loading="isPending || isSuccess"
            @click="createAction"
          >
            Add Action
          </DefaultButton>
        </div>
      </section>
    </div>
  </div>
</template>
